import './unsubscribe.css';

import EventEmitter from 'eventemitter3';
import { Data } from '../../../models/data';
import { Context } from '../../../models/context';
import { LocalizationService } from '../../../services/localization-service';
import { Page } from '../page';
import { IUnsubscribeOptions, IUnsubscribeReason } from '../../../models/context.interface';
import { EventTypes } from '../../../core/event-bus';
import { Util } from '../../../utils/util';
import { ProfileService } from '../../../services/profile-service';
import { TwoWayBindingHelper } from '../../../utils/two-way-binding';
import { FieldType } from '../../../types/enums';
import { InputValidator } from '../../../validators/input-validator';
import { IUnsubscribeDto } from '../../../services/profile-service.interface';

export enum UnsubscribeOption {
    OTHER = 5
}

export interface IUnsubscribe {
    unsubscribeDone: boolean,
    resubscribeDone: boolean,
    reasons: number[],
    other: string
}

/**
 * Handles the unsubscribe and resubscribe UI logic, including rendering state,
 * reacting to language changes, and binding input data.
 */
export class Unsubscribe extends Page {
    private readonly _profileService: ProfileService;
    private readonly _model: IUnsubscribe;
    private readonly _bindingHelper: TwoWayBindingHelper<IUnsubscribe>;
    private readonly _params: IUnsubscribeOptions | undefined;

    /**
     * Constructs the Unsubscribe view logic handler.
     * @param eventBus - The shared application event bus.
     * @param data - Global or route-specific data.
     * @param context - Current page rendering context.
     * @param localizationService - Used for handling language/localization changes.
     * @param profileService - Service for user profile-related operations.
     * @param params - Parameters that describe the unsubscribe context.
     */
    constructor(eventBus: EventEmitter, data: Data, context: Context, localizationService: LocalizationService, profileService: ProfileService, params?: IUnsubscribeOptions) {
        super("page_unsubscribe", eventBus, data, context, localizationService);
        this._profileService = profileService;
        this._params = params;
        this._model = this._initializeModel();
        this._bindingHelper = new TwoWayBindingHelper(this._model);
        this._eventBusListeners();
        this._setEventListeners();
    }

    /**
     * Initializes the default unsubscribe model state.
     * @returns A new IUnsubscribe object with default values.
     */
    private _initializeModel(): IUnsubscribe {
        return {
            unsubscribeDone: false,
            resubscribeDone: false,
            reasons: [],
            other: ""
        };
    }

    /**
     * Registers event listeners to the event bus
     */
    private _eventBusListeners() {
        this._eventBus.on(EventTypes.LANGUAGE_CHANGED, () => {
            this._renderPage();
        });
    }

    /**
     * Rebinds the unsubscribe "Other" reason text field using the binding helper.
     */
    private _setupTwoWayBinding(): void {
        this._bindingHelper.clearListeners();
        this._bindingHelper.bindToInput("other", "txt_unsubscribe_other");
    }

    /**
     * Initializes the unsubscribe logic asynchronously. Placeholder for future async logic.
     */
    public async initAsync(): Promise<void> { }

    /**
     * Shows the page and renders it according to the current unsubscribe state.
     */
    public override show(): void {
        this._hideErrorElements();
        this._renderPage();
        super.show();
    }

    /**
     * Sets DOM click event listeners for unsubscribe and resubscribe buttons.
     */
    private _setEventListeners(): void {
        Util.setElement("onclick", "btn_unsubscribe", this._handleUnsubscribeAsync);
        Util.setElement("onclick", "btn_resubscribe", this._handleResubscribeAsync);
        Util.setElement("onclick", "btn_resubscribe_done", this._handleResubscribeAsync);
    }

    /**
     * Renders the correct unsubscribe state UI based on the current user and subscription status.
     */
    private _renderPage(): void {
        if (!this._params)
            return;

        Util.hideElement("div_unsubscribe_subscribed");
        Util.hideElement("div_unsubscribe_not_subscribed");
        Util.hideElement("div_unsubscribe_done");
        Util.hideElement("div_unsubscribe_resubscribe");
        Util.hideElement("div_unsubscribe_other");

        const showUnsubscribe = this._params.UnsubscribeReasons.length > 0
            ? Util.showElement
            : Util.hideElement;
        showUnsubscribe("div_unsubscribe_reasons");

        if (this._model.unsubscribeDone) {
            Util.showElement("div_unsubscribe_done");
            return;
        }

        if (this._model.resubscribeDone) {
            Util.showElement("div_unsubscribe_resubscribe");
            return;
        }

        if (this._params?.IsSubscribed)
            Util.showElement("div_unsubscribe_subscribed");
        else
            Util.showElement("div_unsubscribe_not_subscribed");

        this._buildUnsubscribeOptions();
        this._setupTwoWayBinding();
    }

    /**
     * Builds and injects the unsubscribe reason checkboxes into the DOM.
     */
    private _buildUnsubscribeOptions(): void {
        const optionsParent = document.getElementById("div_unsubscribe_reasons_options");
        if (!optionsParent)
            return;

        optionsParent.innerHTML = "";

        for (const option of this._params?.UnsubscribeReasons ?? []) {
            const html = this._createReasonElement(option);
            optionsParent.appendChild(html);
        }
    }

    /**
     * Builds an individual unsubscribe reason checkbox DOM element.
     * Attaches a click handler to update selected reasons.
     *
     * @param option - The unsubscribe reason data.
     * @returns The constructed HTML element for the reason.
     */
    private _createReasonElement(option: IUnsubscribeReason): HTMLElement {
        const optionText = `
            <div id="div_unsubscribe_other" class="unsubscribe-other hidden">
                <textarea id="txt_unsubscribe_other" maxlength="160" rows="3" aria-describedby="spn_unsubscribe_other_error"></textarea>
                <span id="spn_unsubscribe_other_error" class="error-text hidden" role="alert">${this._localizationService.names("OtherReason")}</span>
            </div>`;

        const name = this._localizationService.GetNewName(option.names).replace("{{brand}}", this._params!.BrandName);

        const html = `
            <div id="div_unsubscribe_reason_${option.urid}">
                <div class="checkRadio">
                    <input id="chk_unsubscribe_reason_${option.urid}" type="checkbox">
                    <label ltagj="${Util.toLtagj(option.names)}" for="chk_unsubscribe_reason_${option.urid}">
                        ${name}
                    </label>
                </div>
                ${option.urid === UnsubscribeOption.OTHER ? optionText : ""}
            </div>`;

        const element = Util.createHtmlElementFromTemplate(html);
        const input = element.querySelector(`#chk_unsubscribe_reason_${option.urid}`) as HTMLInputElement;

        input.onclick = () => {
            const handler = input.checked ? this._addReason : this._removeReason;
            handler(option.urid);
        };

        return element;
    }

    /**
     * Hides any validation errors for the unsubscribe form.
     */
    private _hideErrorElements(): void {
        Util.hideElementErrors("txt_unsubscribe_other", "spn_unsubscribe_other_error");
    }

    /**
     * Validates the unsubscribe input fields, specifically the "Other" reason.
     * Shows inline error messages if validation fails.
     *
     * @returns True if all inputs are valid, false otherwise.
     */
    private _validateInputs(): boolean {
        this._hideErrorElements();

        const validationFields = [
            { type: FieldType.REQUIRED, inputId: "txt_unsubscribe_other", errorId: "spn_unsubscribe_other_error" }
        ];

        return !validationFields
            .map(field => InputValidator.validate(field.type, field.inputId, field.errorId))
            .some(result => result);
    }

    /**
     * Handles the unsubscribe action.
     * If the user selected "Other", validates the input before submitting.
     * Updates the model and re-renders on success.
     */
    private readonly _handleUnsubscribeAsync = async (): Promise<void> => {
        if (!this._params?.IsSubscribed)
            return;

        if (this._model.reasons.some(x => x === UnsubscribeOption.OTHER) && !this._validateInputs())
            return;

        const payload: IUnsubscribeDto = {
            storeKey: this._data.getBrandProperty("StoreInfo").storeKey,
            profileId: this._params.ProfileId,
            campaignRunId: this._params.CampaignRunId,
            reasons: this._model.reasons,
            otherReason: this._model.other
        };

        const success = await this._profileService.unsubscribe(payload);
        if (success) {
            this._params.IsSubscribed = false;
            this._model.unsubscribeDone = true;
            this._model.reasons = [];
            this._renderPage();
        }
    }

    /**
     * Handles the resubscribe action.
     * Calls the profile service and updates the model on success.
     */
    private readonly _handleResubscribeAsync = async (): Promise<void> => {
        if (!this._params || this._params?.IsSubscribed)
            return;

        const success = await this._profileService.resubscribe(this._data.getBrandProperty("StoreInfo").storeKey, this._params.ProfileId);
        if (success) {
            this._params.IsSubscribed = true;
            this._model.unsubscribeDone = false;
            this._model.resubscribeDone = true;
            this._renderPage();
        }
    }

    /**
     * Adds a selected unsubscribe reason to the model.
     * If "Other" is selected, reveals the text input.
     *
     * @param reasonId - The ID of the unsubscribe reason.
     */
    private readonly _addReason = (reasonId: number): void => {
        if (this._model.reasons.includes(reasonId))
            return;

        this._model.reasons.push(reasonId);

        if (reasonId === UnsubscribeOption.OTHER)
            Util.showElement("div_unsubscribe_other");
    }

    /**
     * Removes a previously selected unsubscribe reason from the model.
     * If "Other" is removed, hides the text input and its label.
     *
     * @param reasonId - The ID of the unsubscribe reason to remove.
     */
    private readonly _removeReason = (reasonId: number): void => {
        this._model.reasons = this._model.reasons.filter(x => x !== reasonId);

        if (reasonId === UnsubscribeOption.OTHER) {
            Util.hideElement("div_unsubscribe_other");
            this._hideErrorElements();
        }
    }
}